/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * One subject's active connections to an MCP server.
 */
export type McpServerUsageSubject = {
  /**
   * Self-reported names of the MCP clients behind the sessions. Unverified.
   */
  clientNames: Array<string>;
  /**
   * Active sessions this subject holds.
   */
  connectionCount: number;
  /**
   * Resolved human-readable name of the subject, if known.
   */
  displayName?: string | undefined;
  /**
   * Email of the subject when it is a Speakeasy user.
   */
  email?: string | undefined;
  /**
   * Most recent use across this subject's sessions, accurate to within five minutes. Absent when unknown.
   */
  lastUsedAt?: Date | undefined;
  /**
   * Avatar URL of the subject when it is a Speakeasy user with one.
   */
  photoUrl?: string | undefined;
  /**
   * Whether every one of this subject's sessions was issued for the whole shared sign-in rather than this server, so the subject may be using another server instead.
   */
  sharedSignIn: boolean;
  /**
   * Subject kind: 'user', 'apikey', 'agent', 'anonymous', or 'workload'.
   */
  subjectType: string;
  /**
   * The subject URN (user:<id> | apikey:<uuid> | agent:<uuid> | anonymous:<id> | workload:<issuer-id>:<external-subject>).
   */
  subjectUrn: string;
};

/** @internal */
export const McpServerUsageSubject$inboundSchema: z.ZodMiniType<
  McpServerUsageSubject,
  unknown
> = z.pipe(
  z.object({
    client_names: z.array(z.string()),
    connection_count: z.int(),
    display_name: z.optional(z.string()),
    email: z.optional(z.string()),
    last_used_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    photo_url: z.optional(z.string()),
    shared_sign_in: z.boolean(),
    subject_type: z.string(),
    subject_urn: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "client_names": "clientNames",
      "connection_count": "connectionCount",
      "display_name": "displayName",
      "last_used_at": "lastUsedAt",
      "photo_url": "photoUrl",
      "shared_sign_in": "sharedSignIn",
      "subject_type": "subjectType",
      "subject_urn": "subjectUrn",
    });
  }),
);

export function mcpServerUsageSubjectFromJSON(
  jsonString: string,
): SafeParseResult<McpServerUsageSubject, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => McpServerUsageSubject$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'McpServerUsageSubject' from JSON`,
  );
}
